import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './UpcomingReleases.css';

const UpcomingReleases = () => {
  const [upcomingReleases, setUpcomingReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRelease, setSelectedRelease] = useState(null);

  useEffect(() => {
    // Simulate API call to get upcoming releases
    const fetchUpcomingReleases = async () => {
      try {
        // Mock data - replace with actual API call
        const mockReleases = [
          {
            id: 1,
            title: "QUANTUM DRIFT",
            type: "EP",
            cover: "/api/placeholder/300/300",
            releaseDate: "2025-02-15",
            status: "mastering",
            description: "An exploration of cosmic soundscapes through the lens of quantum mechanics.",
            trackCount: 5,
            estimatedDuration: "22:45",
            genre: ["Ambient", "Electronic"],
            preOrderLink: "#"
          },
          {
            id: 2,
            title: "STEEL RESONANCE",
            type: "Single",
            cover: "/api/placeholder/300/300",
            releaseDate: "2025-01-20",
            status: "mixing",
            description: "A heavy industrial track featuring mechanical rhythms and metallic textures.",
            trackCount: 1,
            estimatedDuration: "4:12",
            genre: ["Industrial", "Synthwave"],
            preOrderLink: "#"
          },
          {
            id: 3,
            title: "NEON NIGHTS ALBUM",
            type: "Album",
            cover: "/api/placeholder/300/300",
            releaseDate: "2025-04-10",
            status: "production",
            description: "A full-length album diving deep into cyberpunk aesthetics and futuristic soundscapes.",
            trackCount: 12,
            estimatedDuration: "48:30",
            genre: ["Synthwave", "Cyberpunk", "Electronic"],
            preOrderLink: "#"
          }
        ];
        
        setUpcomingReleases(mockReleases);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch upcoming releases:', error);
        setLoading(false);
      }
    };

    fetchUpcomingReleases();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'production':
        return 'var(--accent-secondary)';
      case 'mixing':
        return 'var(--accent-primary)';
      case 'mastering':
        return '#00ff88';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'production':
        return 'IN PRODUCTION';
      case 'mixing':
        return 'MIXING';
      case 'mastering':
        return 'MASTERING';
      default:
        return 'UNKNOWN';
    }
  };

  const getDaysUntilRelease = (releaseDate) => {
    const today = new Date();
    const release = new Date(releaseDate);
    const diffTime = release - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <section className="upcoming-releases upcoming-releases--loading">
        <div className="upcoming-releases__loader">
          <div className="upcoming-releases__spinner"></div>
          <p>SCANNING FOR UPCOMING TRANSMISSIONS...</p>
        </div>
      </section>
    );
  }

  return (
    <motion.section 
      className="upcoming-releases"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="upcoming-releases__container">
        <motion.div 
          className="upcoming-releases__header"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="upcoming-releases__title">
            <span className="upcoming-releases__prefix">UPCOMING_</span>
            TRANSMISSIONS
          </h2>
          <div className="upcoming-releases__status">
            <span className="upcoming-releases__status-dot"></span>
            {upcomingReleases.length} SIGNALS DETECTED
          </div>
        </motion.div>

        <div className="upcoming-releases__grid">
          {upcomingReleases.map((release, index) => {
            const daysUntil = getDaysUntilRelease(release.releaseDate);
            
            return (
              <motion.div
                key={release.id}
                className="upcoming-releases__card"
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.1 * index,
                  type: "spring",
                  stiffness: 200
                }}
                whileHover={{ 
                  y: -10, 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                onClick={() => setSelectedRelease(release)}
              >
                <div className="upcoming-releases__card-inner">
                  <div className="upcoming-releases__cover">
                    <img 
                      src={release.cover} 
                      alt={release.title}
                      onError={(e) => {
                        e.target.src = '/api/placeholder/300/300';
                      }}
                    />
                    <div className="upcoming-releases__overlay">
                      <div className="upcoming-releases__preview-icon">
                        <svg viewBox="0 0 24 24" width="24" height="24">
                          <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        </svg>
                      </div>
                    </div>
                    
                    <div className="upcoming-releases__countdown">
                      <span className="upcoming-releases__countdown-number">
                        {daysUntil > 0 ? daysUntil : 'OUT'}
                      </span>
                      <span className="upcoming-releases__countdown-label">
                        {daysUntil > 0 ? (daysUntil === 1 ? 'DAY' : 'DAYS') : 'NOW'}
                      </span>
                    </div>
                  </div>

                  <div className="upcoming-releases__info">
                    <div className="upcoming-releases__meta">
                      <span className="upcoming-releases__type">{release.type}</span>
                      <span 
                        className="upcoming-releases__status-badge"
                        style={{ backgroundColor: getStatusColor(release.status) }}
                      >
                        {getStatusText(release.status)}
                      </span>
                    </div>

                    <h3 className="upcoming-releases__track-title">{release.title}</h3>
                    
                    <div className="upcoming-releases__details">
                      <div className="upcoming-releases__detail">
                        <span className="upcoming-releases__detail-label">Release:</span>
                        <span className="upcoming-releases__detail-value">
                          {new Date(release.releaseDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="upcoming-releases__detail">
                        <span className="upcoming-releases__detail-label">Tracks:</span>
                        <span className="upcoming-releases__detail-value">{release.trackCount}</span>
                      </div>
                    </div>

                    <p className="upcoming-releases__description">
                      {release.description}
                    </p>

                    <div className="upcoming-releases__genres">
                      {release.genre.map((genre) => (
                        <span key={genre} className="upcoming-releases__genre-tag">
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="upcoming-releases__system-info">
                    <div className="upcoming-releases__system-status">
                      <span>ID:</span>
                      <span>{release.id.toString().padStart(4, '0')}</span>
                    </div>
                    <div className="upcoming-releases__system-status">
                      <span>EST:</span>
                      <span>{release.estimatedDuration}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Release Detail Modal */}
        <AnimatePresence>
          {selectedRelease && (
            <motion.div
              className="upcoming-releases__modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedRelease(null)}
            >
              <motion.div
                className="upcoming-releases__modal"
                initial={{ scale: 0.8, opacity: 0, rotateX: -15 }}
                animate={{ scale: 1, opacity: 1, rotateX: 0 }}
                exit={{ scale: 0.8, opacity: 0, rotateX: 15 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button 
                  className="upcoming-releases__close"
                  onClick={() => setSelectedRelease(null)}
                >
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>

                <div className="upcoming-releases__modal-content">
                  <div className="upcoming-releases__modal-header">
                    <img 
                      src={selectedRelease.cover} 
                      alt={selectedRelease.title}
                      className="upcoming-releases__modal-cover"
                    />
                    <div className="upcoming-releases__modal-info">
                      <h2>{selectedRelease.title}</h2>
                      <p className="upcoming-releases__modal-type">{selectedRelease.type}</p>
                      <p className="upcoming-releases__modal-description">
                        {selectedRelease.description}
                      </p>
                      <div className="upcoming-releases__modal-details">
                        <div className="upcoming-releases__modal-detail">
                          <span>Release Date:</span>
                          <span>{new Date(selectedRelease.releaseDate).toLocaleDateString()}</span>
                        </div>
                        <div className="upcoming-releases__modal-detail">
                          <span>Tracks:</span>
                          <span>{selectedRelease.trackCount}</span>
                        </div>
                        <div className="upcoming-releases__modal-detail">
                          <span>Duration:</span>
                          <span>{selectedRelease.estimatedDuration}</span>
                        </div>
                        <div className="upcoming-releases__modal-detail">
                          <span>Status:</span>
                          <span style={{ color: getStatusColor(selectedRelease.status) }}>
                            {getStatusText(selectedRelease.status)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="upcoming-releases__modal-actions">
                        <a 
                          href={selectedRelease.preOrderLink}
                          className="upcoming-releases__pre-order-btn"
                        >
                          PRE-ORDER
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
};

export default UpcomingReleases;